import React, { useState, useEffect } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { toast } from 'react-toastify';

/**
 * Seating step for a table: capture the guest count, and optionally check in a customer -
 * either the reservation already mapped to this table for today, another of today's
 * reservations picked from a dropdown, or an explicit Walk-in. The choice is persisted
 * alongside the people count (SET_TABLE_PEOPLE) so the eventual order for this table can be
 * attributed to the right customer profile without the waiter re-entering it at checkout.
 *
 * Laid out as a narrow vertical card (mirroring the login screen's `.loginForm`/`.formStyle`
 * look) rather than the wide modal-xl dialog every other modal in the app uses - the width
 * constraint lives on an inner wrapper here, not on the shared CommonModal chrome, so no other
 * modal is affected.
 */
const PeopleCountModal = ({ isOpen, tableId, tableName, onClose, onSuccess, onNext }) => {
  const MAX_PEOPLE_DIGITS = 3;
  const [people, setPeople] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [checkinOptions, setCheckinOptions] = useState({ mapped: null, others: [] });
  const [selection, setSelection] = useState('walkin'); // 'mapped' | 'other' | 'walkin'
  const [selectedOtherId, setSelectedOtherId] = useState('');

  useEffect(() => {
    if (!isOpen || !tableId) return;

    setPeople('');
    setSelection('walkin');
    setSelectedOtherId('');
    setCheckinOptions({ mapped: null, others: [] });

    setLoadingOptions(true);
    callAPI(apiUrls.RESERVATION_CHECKIN_OPTIONS, { table_id: tableId }, 'GET')
      .then((res) => {
        const mapped = res.data?.mapped || null;
        const others = res.data?.others || [];
        setCheckinOptions({ mapped, others });
        if (mapped) {
          setSelection('mapped');
          if (mapped.guests) setPeople(String(mapped.guests));
        }
      })
      .catch((err) => {
        console.error('Failed to load check-in options', err);
      })
      .finally(() => setLoadingOptions(false));
  }, [isOpen, tableId]);

  const handleKeypadDigit = (digit) => {
    setPeople((prev) => {
      if (prev.length >= MAX_PEOPLE_DIGITS) return prev;
      if (prev === '' && digit === '0') return prev; // no leading zero
      return prev + digit;
    });
  };

  const handleKeypadBackspace = () => setPeople((prev) => prev.slice(0, -1));
  const handleKeypadClear = () => setPeople('');

  const handleSubmit = async () => {
    if (!people || isNaN(people) || Number(people) <= 0) {
      toast.error('Enter valid number of people');
      return;
    }

    let customerId = null;
    let reservationId = null;
    let isWalkIn = true;

    if (selection === 'mapped' && checkinOptions.mapped) {
      customerId = checkinOptions.mapped.customer_id;
      reservationId = checkinOptions.mapped.reservation_id;
      isWalkIn = false;
    } else if (selection === 'other' && selectedOtherId) {
      const other = checkinOptions.others.find((o) => String(o.reservation_id) === String(selectedOtherId));
      if (other) {
        customerId = other.customer_id;
        reservationId = other.reservation_id;
        isWalkIn = false;
      }
    }

    setLoading(true);

    const formData = new FormData();
    formData.append('table_id', tableId);
    formData.append('people', people);
    if (customerId) formData.append('customer_id', customerId);
    if (reservationId) formData.append('reservation_id', reservationId);
    formData.append('is_walk_in', isWalkIn ? '1' : '0');

    try {
      await callAPI(apiUrls.SET_TABLE_PEOPLE, {}, 'POST', formData, {});
      toast.success('People count saved');
    } catch (err) {
      toast.error('Failed to save people count');
    } finally {
      setLoading(false);
      onSuccess(Number(people));
      onClose();
      onNext();
    }
  };

  const { mapped, others } = checkinOptions;

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="peopleCountModal">
      <div className="checkinFormCard">
        <h4 className="lgTitle pb-3 text-center">Seat Table {tableName}</h4>

        <div className="formStyle">
          <div className="row gx-2 gy-3">
            <div className="col-12">
              <label className="form-label">Number of People</label>
              <div className="numberKeypad">
                <div className="numberKeypadDisplay">
                  {people || <span className="numberKeypadPlaceholder">Enter number of people</span>}
                </div>
                <div className="numberKeypadGrid">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                    <button
                      type="button"
                      key={digit}
                      className="numberKeypadBtn"
                      onClick={() => handleKeypadDigit(digit)}
                    >
                      {digit}
                    </button>
                  ))}
                  <button type="button" className="numberKeypadBtn numberKeypadBtn--clear" onClick={handleKeypadClear}>
                    Clear
                  </button>
                  <button type="button" className="numberKeypadBtn" onClick={() => handleKeypadDigit('0')}>
                    0
                  </button>
                  <button
                    type="button"
                    className="numberKeypadBtn numberKeypadBtn--backspace"
                    onClick={handleKeypadBackspace}
                    aria-label="Backspace"
                  >
                    ⌫
                  </button>
                </div>
              </div>
            </div>

            <div className="col-12">
              <label className="form-label d-block mb-2">Customer</label>

              {loadingOptions ? (
                <p className="mb-0" style={{ opacity: 0.7 }}>Loading bookings...</p>
              ) : (
                <div className="checkinOptionList">
                  {mapped && (
                    <label className="checkinOptionCard">
                      <input
                        type="radio"
                        name="checkinSelection"
                        checked={selection === 'mapped'}
                        onChange={() => setSelection('mapped')}
                      />
                      <span>
                        <strong>{mapped.name || 'Guest'}</strong>
                        <span className="checkinOptionMeta">
                          {mapped.mobile ? `${mapped.mobile} · ` : ''}{mapped.guests} guests · This table's booking
                        </span>
                      </span>
                    </label>
                  )}

                  {others.length > 0 && (
                    <label className="checkinOptionCard">
                      <input
                        type="radio"
                        name="checkinSelection"
                        checked={selection === 'other'}
                        onChange={() => setSelection('other')}
                      />
                      <span className="w-100">
                        <strong>Other customer</strong>
                        {selection === 'other' && (
                          <select
                            className="form-control mt-2"
                            value={selectedOtherId}
                            onChange={(e) => setSelectedOtherId(e.target.value)}
                          >
                            <option value="">Select a booking</option>
                            {others.map((o) => (
                              <option key={o.reservation_id} value={o.reservation_id}>
                                {o.name || 'Guest'} · {o.time} · {o.guests} guests
                                {o.current_table_name ? ` · currently at Table ${o.current_table_name}` : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </span>
                    </label>
                  )}

                  <label className="checkinOptionCard">
                    <input
                      type="radio"
                      name="checkinSelection"
                      checked={selection === 'walkin'}
                      onChange={() => setSelection('walkin')}
                    />
                    <span><strong>Walk-in</strong></span>
                  </label>
                </div>
              )}
            </div>

            <div className="col-12 text-center pt-2">
              <button className="btn btn-secondary me-2" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn iconBtn btnPrimary"
                onClick={handleSubmit}
                disabled={loading}
              >
                <span>{loading ? 'Saving...' : 'Confirm'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

export default PeopleCountModal;
